import {
  Box,
  Button,
  Card,
  Inline,
  List,
  Modal,
  notifyError,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useKollenoGenerateURL } from '@omnipresentgroup/domains';
import type { InvoiceReport } from '@omnipresentgroup/types';
import { useEffect, useMemo } from 'react';
import KollenoBrandLogo from './../../../../assets/kolleno-brand-logo.png';
import { useManagerUser } from '../../../hooks';
import { WrappedMixpanel } from '../../../mixpanelWrapper';

export const KollenoModal = ({
  closeFn,
  open,
  invoice,
}: {
  closeFn: () => void;
  open: boolean;
  invoice?: InvoiceReport;
}) => {
  const { userId, companyId } = useManagerUser();

  const { isLoading, data, isError, isSuccess } = useKollenoGenerateURL({
    companyId,
    enabled: !!companyId && open,
  });

  const mixPanelProperties = useMemo(
    () => ({
      companyId,
      userId,
      invoiceId: invoice?.id,
      memo: invoice?.reference,
    }),
    [companyId, userId, invoice],
  );

  useEffect(() => {
    if (isError) {
      notifyError({
        title: 'Error',
        description:
          'Something went wrong while generating your unique URL for the Kolleno Portal. Please contact support.',
      });
    }
  }, [isError]);

  return (
    <Modal
      open={open}
      span={5}
      onOpenChange={() => {
        closeFn();
      }}
    >
      <Modal.Header title="Pay online with Kolleno" />

      <Modal.Body>
        <Stack justify="space-between">
          <Inline justify="center">
            <img src={KollenoBrandLogo} alt="kolleno logo" />
          </Inline>
          <Typography as="span" size="16">
            We&apos;ve partnered with Kolleno to make the process of paying your
            invoices online faster, and more streamlined.
          </Typography>
          <Card p="16">
            <List
              gap="12"
              items={[
                {
                  id: '1',
                  detail: 'Navigate to the Kolleno portal below',
                  subDetail: '(no login required)',
                },
                {
                  id: '2',
                  detail: 'View and select the invoices you wish to pay',
                },
                {
                  id: '3',
                  detail: 'Select your bank and complete your payment',
                },
              ]}
              keyExtractor="id"
              render={(
                { detail, subDetail }: { detail: string; subDetail?: string },
                index: number,
              ) => (
                <Inline gap="16" align="center">
                  <Box
                    h={32}
                    w={32}
                    bg="brand"
                    radius="full"
                    css={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography as="span" weight="medium" size="14">
                      {index + 1}
                    </Typography>
                  </Box>

                  <Inline gap="4">
                    <Typography as="span" size="14" weight="medium">
                      {detail}
                    </Typography>
                    {subDetail && (
                      <Typography as="span" size="14">
                        {subDetail}
                      </Typography>
                    )}
                  </Inline>
                </Inline>
              )}
            />
          </Card>

          <Typography as="span" size="16">
            Your invoice status will then be updated back in the Omniplatform
            (this process may take a few hours).
          </Typography>
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            WrappedMixpanel.track(
              'Pay with Kolleno Modal Cancelled',
              mixPanelProperties,
            );
            closeFn();
          }}
        >
          Cancel
        </Button>
        <Button
          icon="ExternalLink"
          loading={isLoading}
          disabled={!isSuccess}
          onClick={() => {
            WrappedMixpanel.track(
              'Pay with Kolleno Clicked',
              mixPanelProperties,
            );
            data?.url && window.open(data.url, '_blank');
          }}
        >
          Pay with Kolleno
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
